// TextInput.tsx
import React, { useState, useEffect } from "react";
import { useField } from "react-final-form";
import clsx from "clsx";

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  field: string;
  label?: string;
  trailingChildren?: React.ReactNode[];
  inputWidth?: string; // e.g. w-[100px]
  inputHeight?: string; // e.g. h-[40px]
  inputBorder?: string; // e.g. "border border-solid border-neutral-300"
  inputBgColor?: string; // e.g. bg-neutral-0
  inputTextColor?: string; // e.g. text-gray-900
  inputTextSize?: string; // e.g. text-[14px]
  labelTextColor?: string; // e.g. text-gray-900
}

export function TextInputBase({
  id,
  type = "text",
  field,
  label,
  placeholder,
  required,
  onChange,
  trailingChildren,
  inputWidth = "w-full",
  inputHeight,
  inputBorder = "border-0",
  inputBgColor,
  inputTextColor = "text-gray-900",
  inputTextSize,
  labelTextColor = "text-gray-900",
  value,
  ...others
}: TextInputProps & {
  value: string;
}) {
  const inputClasses = clsx(
    `
    block px-4 rounded-md py-1.5 sm:leading-6
    shadow-sm ring-1 ring-inset ring-gray-300
    focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none
    disabled:placeholder:text-gray-300 disabled:ring-gray-200 disabled:text-gray-400 disabled:bg-neutral-0
    placeholder:text-gray-400`,
    trailingChildren && `rounded-none rounded-l-md`,
    inputWidth,
    inputHeight,
    inputBorder,
    inputBgColor,
    inputTextColor,
    inputTextSize,
  );

  return (
    <div>
      {label && (
        <label
          htmlFor={field}
          className={clsx("block text-sm font-medium leading-6", labelTextColor)}
        >
          {label}
        </label>
      )}
      <div className="relative flex flex-grow items-stretch focus-within:z-10">
        <input
          id={id}
          type={type}
          name={field}
          className={inputClasses}
          placeholder={placeholder}
          onChange={onChange}
          required={required}
          value={value}
          {...others}
        />
        {trailingChildren && <>{trailingChildren}</>}
      </div>
    </div>
  );
}

const TextInput = ({ field, onChange, ...others }: TextInputProps) => {
  const f = useField(field);
  const initialValue = f.input.value || f.input.defaultValue;
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    const formValue = f.input.value || f.input.defaultValue;
    if (formValue !== value) {
      setValue(formValue);
    }
  }, [f.input.value, f.input.defaultValue]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    // Call react-final-form's onChange to update form state
    f.input.onChange(e);
    // Call optional custom onChange prop if provided
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <TextInputBase
      {...f.input}
      onChange={onChangeHandler}
      value={value}
      field={field}
      {...others}
    />
  );
};

export default TextInput;
